import * as React from 'react';

/**
 * Global error boundary (PROJECT-ANALYSIS Phase 1, item 7).
 *
 * Before this existed, any render throw anywhere in the tree unmounted the
 * entire app: a caller mid-allocation got a white screen whose only recovery
 * was editing the URL by hand.
 *
 * The fallback is deliberately plain DOM with inline styles -- if the crash
 * came from sha-el-design itself, the fallback must not depend on it.
 */
export class ErrorBoundary extends React.Component<{}, State> {
  constructor(props: {}) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Surface the real stack in the console -- the UI stays calm, the
    // diagnosis stays possible.
    // tslint:disable-next-line no-console
    console.error('Unhandled render error:', error, info.componentStack);
  }

  render() {
    if (!this.state.error) {
      return this.props.children;
    }
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          background: '#F4F7FB',
          fontFamily: 'inherit',
        }}
      >
        <div
          style={{
            maxWidth: '440px',
            width: '100%',
            background: '#fff',
            border: '1px solid rgba(15,37,71,0.10)',
            borderRadius: '14px',
            boxShadow: '0 12px 32px rgba(15,37,71,0.10)',
            padding: '32px 28px',
            textAlign: 'center',
          }}
        >
          <h2 style={{ margin: '0 0 8px', color: '#0F2547' }}>Something went wrong</h2>
          <p style={{ margin: '0 0 22px', color: '#52658A', lineHeight: 1.5 }}>
            The page hit an unexpected error. Work you already saved -- including
            recorded calls -- is not affected.
          </p>
          <button
            onClick={() => location.reload()}
            style={{
              padding: '10px 22px',
              marginRight: '10px',
              border: 'none',
              borderRadius: '9px',
              background: '#1E40AF',
              color: '#fff',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Reload
          </button>
          <a
            href='/dashboard/start_calling/'
            style={{ color: '#1E6FD9', fontSize: '14px', fontWeight: 600 }}
          >
            Go to Work List
          </a>
        </div>
      </div>
    );
  }
}

interface State {
  error: Error;
}
